import React, { useEffect, useState } from 'react';
import { Webhook, Clock, Play, RotateCcw, Boxes, KeyRound } from 'lucide-react';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import type { WorkflowTriggerView } from '@cotera/client/app/etc/api/typed-client';

/** `workflow_runs.trigger_type` value for runs started by the "Test webhook" button. */
export const WORKFLOW_WEBHOOK_TEST_TRIGGER_TYPE = 'webhook_test';

/** `workflow_runs.trigger_type` value for runs started by the "Trigger now" button. */
export const WORKFLOW_CRON_TEST_TRIGGER_TYPE = 'cron_test';

/** `workflow_runs.trigger_type` for runs started from an unsaved editor draft. */
export const WORKFLOW_DRAFT_TEST_TRIGGER_TYPE = 'draft_test';

/** `workflow_runs.trigger_type` for runs started by a dataset row trigger. */
export const WORKFLOW_DATASET_TRIGGER_TYPE = 'dataset';

/** `workflow_runs.trigger_type` for the single run a bucket spawns when it flushes. */
export const WORKFLOW_BUCKET_FLUSH_TRIGGER_TYPE = 'bucket_flush';

/** Run statuses that mean the run is still in progress (non-terminal). */
export function isActiveRunStatus(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'incomplete' || status === 'running';
}

/** Both production and test webhook deliveries render as the same webhook trigger. */
const isWebhookTriggerType = (triggerType: string): boolean =>
  triggerType === 'webhook' ||
  triggerType === WORKFLOW_WEBHOOK_TEST_TRIGGER_TYPE;

const isCronTriggerType = (triggerType: string): boolean =>
  triggerType === 'cron' || triggerType === WORKFLOW_CRON_TEST_TRIGGER_TYPE;

type BadgeVariant = React.ComponentProps<typeof Badge>['variant'];

/** Maps a workflow run status to a Badge variant + display label. */
const RUN_STATUS_PRESENTATION: Record<
  string,
  { variant: BadgeVariant; label: string }
> = {
  queued: { variant: 'blue', label: 'Queued' },
  // Step-level only: runs roll `running` up into `incomplete`.
  running: { variant: 'amber', label: 'Running' },
  incomplete: { variant: 'amber', label: 'In progress' },
  completed: { variant: 'green', label: 'Completed' },
  failed: { variant: 'destructive', label: 'Failed' },
  cancelled: { variant: 'muted', label: 'Cancelled' },
  stopped: { variant: 'gray', label: 'Stopped' },
  // Step-level: a bucket collected this run's branch. Runs roll `absorbed` up
  // into `completed` (see `syncRunStatus`), so this is mainly the run-graph step
  // badge — a sibling of `stopped` (terminal, branch ended here, non-failing).
  //
  // `gray`, with stopped and cancelled, on purpose. Colour here is a state
  // class, not a state — blue/amber in flight, green succeeded, destructive
  // needs you, grey terminal and benign — and absorbed is terminal and benign.
  // The label already says "Absorbed" rather than "Stopped", on a card already
  // showing the bucket, so a hue of its own would be redundant and would read
  // as "look here" when there is nothing to do.
  absorbed: { variant: 'gray', label: 'Absorbed' },
};

/**
 * What a status means, for statuses whose name does not carry it.
 *
 * Only `absorbed` has one. It is the single status in this map that is not
 * ordinary English — everything else (Queued, Running, Failed) explains itself,
 * and a tooltip on those would be noise.
 */
const RUN_STATUS_HINT: Record<string, string> = {
  absorbed:
    'A bucket collected this branch. Nothing failed — the steps below it run later, in a flush run.',
};

/** Human-readable label for a run status (falls back to the raw status). */
export function runStatusLabel(status: string): string {
  return RUN_STATUS_PRESENTATION[status]?.label ?? status;
}

export const WorkflowRunStatusBadge: React.FC<{
  status: string;
  size?: 'default' | 'sm' | 'lg';
}> = ({ status, size = 'sm' }) => {
  const presentation = RUN_STATUS_PRESENTATION[status] ?? {
    variant: 'gray' as const,
    label: status,
  };
  const hint = RUN_STATUS_HINT[status];
  return (
    <Badge variant={presentation.variant} size={size} title={hint}>
      {presentation.label}
    </Badge>
  );
};

/** Marks runs started from the "Test webhook" button so they read as non-production. */
export const WorkflowRunTestBadge: React.FC<{
  size?: 'default' | 'sm' | 'lg';
}> = ({ size = 'sm' }) => (
  <Badge variant="purple" size={size}>
    <Play className="h-3 w-3" />
    Test
  </Badge>
);

/**
 * Marks a run as the Nth attempt of a retry chain. Renders nothing for a first
 * attempt. `gray` so it never reads as a status or collides with the Test badge.
 */
export const WorkflowRunAttemptBadge: React.FC<{
  retryNum: number;
  size?: 'default' | 'sm' | 'lg';
}> = ({ retryNum, size = 'sm' }) =>
  retryNum <= 0 ? null : (
    <Badge variant="gray" size={size}>
      <RotateCcw className="h-3 w-3" />
      Attempt {retryNum + 1}
    </Badge>
  );

/**
 * The resolved idempotency key a run was dispatched under. Renders nothing when
 * the run has none. Unlike the other badges it may shrink: keys are
 * user-templated and can be long, so it truncates (full value on hover) rather
 * than pushing the row's status off the edge.
 */
export const WorkflowRunIdempotencyKeyBadge: React.FC<{
  idempotencyKey: string | null;
  size?: 'default' | 'sm' | 'lg';
}> = ({ idempotencyKey, size = 'sm' }) =>
  idempotencyKey === null || idempotencyKey === '' ? null : (
    <Badge
      variant="slate"
      size={size}
      className="min-w-0 shrink justify-start font-mono font-normal"
      title={`Idempotency key: ${idempotencyKey}`}
    >
      <KeyRound className="h-3 w-3" />
      <span className="truncate">{idempotencyKey}</span>
    </Badge>
  );

/** A human-readable "how it was triggered" label for a run. */
export function triggerLabel(
  triggerType: string,
  trigger: WorkflowTriggerView | undefined
): string {
  const name = trigger?.name;
  if (name !== undefined && name !== '') {
    return name;
  }
  if (isWebhookTriggerType(triggerType)) {
    return 'Webhook';
  }
  if (isCronTriggerType(triggerType)) {
    const schedule = trigger?.cron?.schedule;
    return schedule !== undefined && schedule !== ''
      ? `Schedule (${schedule})`
      : 'Schedule';
  }
  if (triggerType === WORKFLOW_BUCKET_FLUSH_TRIGGER_TYPE) {
    return 'Bucket flush';
  }
  return triggerType;
}

export const WorkflowRunTriggerCell: React.FC<{
  triggerType: string;
  trigger: WorkflowTriggerView | undefined;
}> = ({ triggerType, trigger }) => {
  const Icon = isWebhookTriggerType(triggerType)
    ? Webhook
    : isCronTriggerType(triggerType)
    ? Clock
    : triggerType === WORKFLOW_BUCKET_FLUSH_TRIGGER_TYPE
    ? Boxes
    : Play;
  const label = triggerLabel(triggerType, trigger);
  return (
    <span
      className="inline-flex items-center gap-1.5 text-muted-foreground min-w-0 max-w-full"
      title={label}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate min-w-0">{label}</span>
    </span>
  );
};

/** Short relative time including seconds ("3s ago", "5m ago", "2h ago"). */
export function formatRelativeShort(date: Date): string {
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Live-updating relative timestamp (re-renders every second) so the "X ago"
 * value stays current without a refetch — mirrors the list page's relative time.
 */
export const LiveRelativeTime: React.FC<{
  date: Date;
  className?: string;
  title?: string;
}> = ({ date, className, title }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <span className={className} title={title}>
      {formatRelativeShort(date)}
    </span>
  );
};

/** Formats the duration between two timestamps as a short human string. */
export function formatDuration(
  startedAt: Date | null,
  finishedAt: Date | null
): string {
  if (startedAt === null) {
    return '—';
  }
  const end = finishedAt ?? new Date();
  const ms = end.getTime() - startedAt.getTime();
  if (ms < 0) {
    return '—';
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m ${remSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) {
    return `${hours}h ${remMinutes}m`;
  }
  // Long runs are the ones worth reading closely, and they were the ones this
  // reported worst: a four-day run rendered as "96h 35m".
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h`;
}

/**
 * Elapsed time that keeps counting while the run is still going.
 *
 * {@link formatDuration} samples `new Date()` at render, so on an in-progress
 * run it only advances when something else happens to re-render — sat beside a
 * {@link LiveRelativeTime} that ticks every second, it reads as a stopped clock.
 * Finished runs have a fixed duration and get no interval.
 */
export const LiveDuration: React.FC<{
  startedAt: Date | null;
  finishedAt: Date | null;
  className?: string;
}> = ({ startedAt, finishedAt, className }) => {
  const isRunning = startedAt !== null && finishedAt === null;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) {
      return;
    }
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);
  return (
    <span className={className}>{formatDuration(startedAt, finishedAt)}</span>
  );
};
